import React, { useState } from 'react';

import { useUserMatchScoresState, useUserMatchScoresActions } from './UserMatchScoresContext';
import { getUserMatchScores } from './restApi';
import UserMatchScoresBreakdown from './UserMatchScoresBreakdown';
import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Link from '../../Shared/UI/Link/components/Link';
import Button from '../../Shared/UI/Button/components/Button';
import Field from '../../Shared/UI/Field/components/Field';
import Select from '../../Shared/UI/Select/components/Select';
import styles from './UserMatchScores.module.scss';

const TRUNCATE_AT = 80;
const TABLE_COLUMN_COUNT = 10;

const truncate = (value) => {
  if (value == null) return '';
  const str = String(value);
  return str.length > TRUNCATE_AT ? str.slice(0, TRUNCATE_AT) : str;
};

const GapBadge = ({ blocked, softPenaltyCount }) => {
  if (blocked) {
    return <span className={styles.badgeBlocked}>Blocked</span>;
  }
  if (softPenaltyCount > 0) {
    return <span className={styles.badgePenalty}>Penalty</span>;
  }
  return null;
};

const GapsCell = ({ unmetRequired }) => {
  const items = unmetRequired || [];
  if (items.length === 0) {
    return '—';
  }

  return (
    <>
      {items.map((item, index) => {
        const fullText = item?.text == null ? '' : String(item.text);
        return (
          <span key={index} className={styles.gapLine} title={fullText}>
            {`Missing required: ${truncate(fullText)}`}
          </span>
        );
      })}
    </>
  );
};

const SortIndicator = ({ column, sortBy, sortDirection }) => {
  if (sortBy !== column) return <span className={styles.sortIndicator}>↕</span>;
  return <span className={styles.sortIndicator}>{sortDirection === 'desc' ? '↓' : '↑'}</span>;
};

const UserMatchScoresTable = () => {
  const state = useUserMatchScoresState();
  const { onResponse, onError, openCreateProspectModal } = useUserMatchScoresActions();
  const scores = state?.scores || [];
  const [expandedScoreId, setExpandedScoreId] = useState(null);

  const fetchScores = ({ pageNumber, sortBy, sortDirection, gapFilter }) => {
    getUserMatchScores({
      userId: state?.userId,
      pageNumber,
      sortBy,
      sortDirection,
      gapFilter,
      onResponse,
      onError,
    });
  };

  const handleSort = (column) => {
    const newDirection = state?.sortBy === column && state?.sortDirection === 'desc' ? 'asc' : 'desc';
    fetchScores({
      pageNumber: 1,
      sortBy: column,
      sortDirection: newDirection,
      gapFilter: state?.gapFilter,
    });
  };

  const handleGapFilterChange = (e) => {
    fetchScores({
      pageNumber: 1,
      sortBy: state?.sortBy,
      sortDirection: state?.sortDirection,
      gapFilter: e.target.value,
    });
  };

  const toggleDetails = (scoreId) => {
    setExpandedScoreId((current) => (current === scoreId ? null : scoreId));
  };

  return (
    <>
      <div className={styles.gapFilter}>
        <Field label="Gaps">
          <Select
            value={state?.gapFilter || 'all'}
            onChange={handleGapFilterChange}
          >
            <option value="all">All gaps</option>
            <option value="blocked">Blocked</option>
            <option value="soft_penalty">Soft penalty</option>
          </Select>
        </Field>
      </div>

      {scores.length === 0 && !state?.loading ? (
        <p className={styles.emptyState}>No match scores</p>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>
                <span className={styles.sortableHeader} onClick={() => handleSort('match_score')}>
                  Score <SortIndicator column="match_score" sortBy={state?.sortBy} sortDirection={state?.sortDirection} />
                </span>
              </Th>
              <Th>Gaps</Th>
              <Th>Title</Th>
              <Th>Company</Th>
              <Th>Link</Th>
              <Th>Board</Th>
              <Th>
                <span className={styles.sortableHeader} onClick={() => handleSort('created_at')}>
                  Scored At <SortIndicator column="created_at" sortBy={state?.sortBy} sortDirection={state?.sortDirection} />
                </span>
              </Th>
              <Th>Status</Th>
              <Th>Prospect</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {scores.map((score, i) => {
              const scoreId = score.id || `index-${i}`;
              const isExpanded = expandedScoreId === scoreId;
              return (
                <React.Fragment key={scoreId}>
                  <Tr>
                    <Td>
                      <span className={score.aboveThreshold ? styles.scoreAboveThreshold : styles.scoreBelowThreshold}>
                        {score.matchScore}
                      </span>
                      <GapBadge blocked={score.blocked} softPenaltyCount={score.softPenaltyCount} />
                    </Td>
                    <Td>
                      <GapsCell unmetRequired={score.unmetRequired} />
                    </Td>
                    <Td>{score.title || '—'}</Td>
                    <Td>{score.companyName || '—'}</Td>
                    <Td>
                      {score.sourceUrl ? (
                        <Link href={score.sourceUrl} external size="sm">View</Link>
                      ) : '—'}
                    </Td>
                    <Td>
                      {score.biopharmaGroupId ? (
                        <>
                          <Link href={`/biopharma_groups/${score.biopharmaGroupId}`} external size="sm">Board</Link>
                          {score.externalJobBoardAasmState && (
                            <span className={styles.boardState}>{score.externalJobBoardAasmState.replace(/_/g, ' ')}</span>
                          )}
                        </>
                      ) : '—'}
                    </Td>
                    <Td>{score.createdAt}</Td>
                    <Td>{score.closed ? 'Closed' : 'Open'}</Td>
                    <Td>
                      {score.jobProspectId ? (
                        <span className={styles.prospectCreated}>Created</span>
                      ) : (
                        <Button size="sm" onClick={() => openCreateProspectModal(score)}>
                          Create
                        </Button>
                      )}
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        variant="secondary"
                        pressed={isExpanded}
                        onClick={() => toggleDetails(scoreId)}
                      >
                        Details
                      </Button>
                    </Td>
                  </Tr>
                  {isExpanded && (
                    <Tr>
                      <Td colSpan={TABLE_COLUMN_COUNT}>
                        <UserMatchScoresBreakdown score={score} />
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      )}
    </>
  );
};

export default UserMatchScoresTable;
